/**
 * Temporary helpers for the Elasticsearch → ClickHouse contexts API
 * migration (gated on use-contexts-in-ewa serving `enabled`). They shape
 * search params per backend so call sites stay flag-agnostic, and are
 * deleted with the flag once the ClickHouse rollout completes.
 */
import { enableClickhouseContextsApiChangesInTheUi } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { Match, Queryfilter } from '@gonfalon/queryfilter/builder';
import { and, empty, isMatchNode } from '@gonfalon/queryfilter/builder';

/**
 * Built-in filter fields the ClickHouse-backed contexts search API accepts.
 * `kind`/`key`/`applicationid` take the `equals`/`startsWith`/`anyOf`
 * operators; `q` is stricter (see isSupportedContextsSearchClause). Custom
 * attribute clauses are accepted separately (see
 * isSupportedContextsSearchClause).
 *
 * This set is deliberately narrower than what the API now accepts: gonfalon#71041
 * and #71120 added exists, notEquals, before/after, and `or` groups, but the
 * contexts list filter UI has not been widened to offer them. The instance-level
 * aliases those PRs added are handled separately (see KIND_ALIAS_FIELD_OPERATORS).
 */
const BUILT_IN_FILTER_FIELDS = new Set(['kind', 'key', 'q', 'applicationid']);

/**
 * Match operators the built-in fields other than `q` accept.
 */
const BUILT_IN_FILTER_OPERATORS = new Set(['equals', 'startsWith', 'anyOf']);

/**
 * Instance-level fields the public filter docs list for the contexts list, which
 * the ClickHouse backend rewrites to the row's own kind/key (DPLAT-1868;
 * ewa_legacy_field_aliases.go). Operators mirror that rewrite: `contains` must
 * hold exactly one value, and `kindKey`/`kindKeys` values must be `kind:key`.
 */
const KIND_ALIAS_FIELD_OPERATORS: ReadonlyMap<string, ReadonlySet<string>> = new Map([
  ['kinds', new Set(['equals', 'anyOf', 'contains'])],
  ['kindkey', new Set(['equals', 'anyOf', 'notEquals'])],
  ['kindkeys', new Set(['equals', 'anyOf', 'contains'])],
]);

// The attribute-clause rules below mirror the backend's ClickHouse filter
// compiler (gonfalon#67892, DPLAT-1795): attribute fields are
// `<kind>.<attr>`, `*.<attr>`, or bare `name` (shorthand for `*.name`);
// attribute names are single JSON path segments; only `equals` (one string,
// number, or boolean) and `startsWith` (a string) are supported; and a
// filter may contain at most 10 attribute clauses.
const ATTRIBUTE_NAME_REGEX = /^[a-zA-Z_][a-zA-Z0-9_-]*$/;
export const MAX_ATTRIBUTE_FILTER_CLAUSES = 10;
const MAX_ATTRIBUTE_PREFIX_LENGTH = 500;

export function isBuiltInContextsSearchField(field: string): boolean {
  return BUILT_IN_FILTER_FIELDS.has(field.toLowerCase());
}

/** `kinds`, `kindKey`, or `kindKeys`: accepted by the search API but not offered by the filter UI. */
export function isKindAliasContextsSearchField(field: string): boolean {
  return KIND_ALIAS_FIELD_OPERATORS.has(field.toLowerCase());
}

function isKindKeyString(value: unknown): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const separatorIndex = value.indexOf(':');
  return separatorIndex > 0 && separatorIndex < value.length - 1;
}

function isKindAliasContextsSearchClause(match: Match): boolean {
  const field = match.matchname.toLowerCase();
  const operators = KIND_ALIAS_FIELD_OPERATORS.get(field);
  if (operators === undefined || !operators.has(match.matchoperator)) {
    return false;
  }
  const isValue = field === 'kinds' ? (value: unknown) => typeof value === 'string' : isKindKeyString;
  const value = match.matchvalue;
  if (Array.isArray(value)) {
    if (match.matchoperator === 'notEquals') {
      return false;
    }
    const count = match.matchoperator === 'contains' ? value.length === 1 : value.length > 0;
    return count && value.every(isValue);
  }
  return match.matchoperator !== 'anyOf' && match.matchoperator !== 'contains' && isValue(value);
}

/** Whether an attribute name is a single JSON path segment, the only shape the search API can filter on. */
export function isFilterableContextAttributeName(name: string): boolean {
  return ATTRIBUTE_NAME_REGEX.test(name);
}

function splitAttributeField(field: string): { kind: string; attr: string } | undefined {
  if (field.toLowerCase() === 'name') {
    return { kind: '*', attr: 'name' };
  }
  const separatorIndex = field.indexOf('.');
  if (separatorIndex <= 0 || separatorIndex === field.length - 1) {
    return undefined;
  }
  return { kind: field.slice(0, separatorIndex), attr: field.slice(separatorIndex + 1) };
}

function isAttributeContextsSearchClause(match: Match): boolean {
  const attributeField = splitAttributeField(match.matchname);
  if (attributeField === undefined) {
    return false;
  }
  if (attributeField.attr.toLowerCase() !== 'key' && !ATTRIBUTE_NAME_REGEX.test(attributeField.attr)) {
    return false;
  }
  if (match.matchoperator === 'equals') {
    return (
      typeof match.matchvalue === 'string' ||
      typeof match.matchvalue === 'number' ||
      typeof match.matchvalue === 'boolean'
    );
  }
  if (match.matchoperator === 'startsWith') {
    return typeof match.matchvalue === 'string' && match.matchvalue.length <= MAX_ATTRIBUTE_PREFIX_LENGTH;
  }
  return false;
}

/**
 * Whether a single filter clause is one the ClickHouse-backed contexts search
 * API accepts: `q` with equals and a non-empty string, another built-in field
 * (kind/key/applicationid) with equals/startsWith/anyOf, a kind alias field
 * (kinds/kindKey/kindKeys) per KIND_ALIAS_FIELD_OPERATORS, or a custom
 * attribute clause per the rules above. The per-filter cap of 10 attribute
 * clauses is enforced by sanitizeContextsSearchFilter, not here.
 */
export function isSupportedContextsSearchClause(match: Match): boolean {
  const field = match.matchname.toLowerCase();
  // The backend expands `q` into its own multi-field search and only accepts
  // `equals` with a non-empty string value; other operators or values 400.
  if (field === 'q') {
    return match.matchoperator === 'equals' && typeof match.matchvalue === 'string' && match.matchvalue.length > 0;
  }
  if (isBuiltInContextsSearchField(field)) {
    return BUILT_IN_FILTER_OPERATORS.has(match.matchoperator);
  }
  if (isKindAliasContextsSearchField(field)) {
    return isKindAliasContextsSearchClause(match);
  }
  return isAttributeContextsSearchClause(match);
}

/**
 * Sort param for `POST /contexts/search` and `GET /contexts/{kind}/{key}`.
 * The ClickHouse backend ignores sort (and will reject it post-rollout), so
 * this returns `undefined` when the migration flag is on.
 */
export function contextsSearchSort(sort: string | undefined = '-ts'): string | undefined {
  return enableClickhouseContextsApiChangesInTheUi() ? undefined : sort;
}

/**
 * Sort param for `POST /context-instances/search` and `GET /context-instances/{id}`.
 * The ClickHouse backend rejects any sort on these endpoints with a 400, so
 * this returns `undefined` when the migration flag is on.
 */
export function contextInstancesSearchSort(sort: string | undefined = '-ts'): string | undefined {
  return enableClickhouseContextsApiChangesInTheUi() ? undefined : sort;
}

/**
 * `limit` param for the batched contexts search task runners, whose batches
 * carry up to 50 kind:keys while the API's default page size is 20. The
 * legacy Elasticsearch path has never sent `limit` on these requests, so to
 * keep flag-off requests unchanged this forwards it only when the migration
 * flag is on.
 */
export function batchedContextsSearchLimit(limit: number | undefined): number | undefined {
  return enableClickhouseContextsApiChangesInTheUi() ? limit : undefined;
}

/**
 * Removes filter clauses the ClickHouse-backed contexts search API rejects
 * (unsupported fields and operators, `or` groups, and attribute clauses past
 * the API's per-filter cap of 10 — parenthesized groups are unwrapped and
 * their contents sanitized) so stale URL-persisted or caller-built filters
 * don't turn into 400s. Returns the filter unchanged when the migration flag
 * is off.
 */
export function sanitizeContextsSearchFilter(filter: Queryfilter): Queryfilter {
  if (!enableClickhouseContextsApiChangesInTheUi()) {
    return filter;
  }

  let attributeClauses = 0;

  function sanitizeNode(node: Queryfilter): Queryfilter {
    if (node.type === 'empty') {
      return node;
    }

    if (isMatchNode(node)) {
      if (!isSupportedContextsSearchClause(node)) {
        return empty();
      }
      if (!isBuiltInContextsSearchField(node.matchname) && !isKindAliasContextsSearchField(node.matchname)) {
        attributeClauses += 1;
        if (attributeClauses > MAX_ATTRIBUTE_FILTER_CLAUSES) {
          return empty();
        }
      }
      return node;
    }

    if (node.type === 'and') {
      const children = node.children.map(sanitizeNode).filter((child) => child.type !== 'empty');
      return children.length > 0 ? and(children) : empty();
    }

    // A parenthesized group is semantically identical to its single child, and
    // the ClickHouse filter grammar doesn't support parentheses, so sanitize
    // the child and return it unwrapped.
    if (node.type === 'group') {
      return sanitizeNode(node.children);
    }

    // The ClickHouse filter grammar only supports AND combinations, so `or`
    // groups are dropped wholesale.
    return empty();
  }

  return sanitizeNode(filter);
}
